import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Check, X, ChevronLeft, Images } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { products, referenceImages } from "../data/content";

const ProductCategoryPage = ({ category }) => {
  const product = products[category];
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get category images from centralized data using refKey
  const categoryImages = product ? (referenceImages[product.refKey] || []) : [];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === 0 ? categoryImages.length - 1 : prev - 1));
  }, [categoryImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === categoryImages.length - 1 ? 0 : prev + 1));
  }, [categoryImages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") { e.preventDefault(); closeLightbox(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); goToPrevious(); }
      if (e.key === "ArrowRight") { e.preventDefault(); goToNext(); }
    };
    if (lightboxOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  if (!product) {
    return (
      <div className="py-32 text-center">
        <h1 className="text-2xl text-slate-900">Termék nem található</h1>
        <Link to="/termekek" className="text-amber-600 mt-4 inline-block">Vissza a termékekhez</Link>
      </div>
    );
  }

  // Show first 4 images in the grid
  const previewImages = categoryImages.slice(0, 4);
  const hasMoreImages = categoryImages.length > 4;

  return (
    <div data-testid={`product-${category}-page`}>
      <SEO path={`/termekek/${category}`} productCategory={category} />
      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <nav className="text-sm text-slate-400 mb-3">
            <Link to="/" className="hover:text-white">Kezdőlap</Link>
            <span className="mx-2">/</span>
            <Link to="/termekek" className="hover:text-white">Termékek</Link>
            <span className="mx-2">/</span>
            <span className="text-amber-500">{product.name}</span>
          </nav>
          <div className="w-12 h-1 bg-amber-500 mb-3" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {product.name}
          </h1>
          <p className="text-stone-300 mt-2 max-w-2xl text-sm">
            {product.headline}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-slate-600 mb-6">{product.description}</p>
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Jellemzők
              </h3>
              <ul className="space-y-2 mb-6">
                {product.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-slate-600 text-sm">
                    <Check className="w-4 h-4 text-amber-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                asChild
                className="rounded-none px-6 py-4 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase"
              >
                <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 sm:py-12 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Előnyök
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {product.benefits.map((benefit, i) => (
              <div key={i} className="bg-white p-6 border border-stone-200">
                <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery - show first 4 reference images */}
      {categoryImages.length > 0 && (
        <section className="py-10 sm:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Referenciák
              </h2>
              <Button 
                asChild
                variant="outline"
                className="rounded-none px-6 py-3 border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                <Link to={`/referenciak?filter=${product.refKey}`}>
                  <Images className="w-4 h-4 mr-2" />
                  Összes referencia
                </Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {previewImages.map((img, i) => (
                <button
                  key={img.id}
                  onClick={() => openLightbox(i)}
                  className="aspect-square overflow-hidden group cursor-pointer relative"
                  data-testid={`product-ref-image-${img.id}`}
                >
                  <img 
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Show "+N more" overlay on last image if there are more */}
                  {hasMoreImages && i === previewImages.length - 1 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">+{categoryImages.length - previewImages.length}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Érdekli ez a termék?
          </h2>
          <p className="text-amber-100 mb-6 text-sm">
            Kérjen ingyenes helyszíni felmérést és személyre szabott árajánlatot!
          </p>
          <Button 
            asChild
            className="rounded-none px-8 py-5 bg-white text-amber-600 hover:bg-stone-100 font-medium text-sm tracking-wider uppercase"
          >
            <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
          </Button>
        </div>
      </section>

      {/* Lightbox Modal - can browse ALL category images */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <div className="max-w-5xl max-h-[80vh] px-16" onClick={(e) => e.stopPropagation()}>
            <img 
              src={categoryImages[currentImageIndex]?.src}
              alt={categoryImages[currentImageIndex]?.alt}
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            <span className="text-white text-sm">
              {currentImageIndex + 1} / {categoryImages.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
                navigate(`/referenciak?filter=${product.refKey}`);
              }}
              className="text-amber-400 text-sm hover:text-amber-300 underline"
            >
              Összes referencia megtekintése
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategoryPage;
